import {
  type MutableRefObject,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import {
  type PanelImperativeHandle,
  usePanelRef,
} from "react-resizable-panels";

/**
 * The agenda and codes panels can each sit in the right rail or stacked in the
 * note column. Both places size their panels the same way: every panel keeps an
 * explicit height and one "filler" panel absorbs whatever is left. This hook
 * owns that arithmetic for a single vertical group.
 *
 * Dragging is left entirely to `react-resizable-panels`. The hook only resizes
 * panels when the set of panels or their open state changes. Recomputing a
 * layout during a drag would fight the library, which derives each move from
 * the sizes captured at pointer-down — overwriting those mid-drag makes the
 * boundary move against the cursor instead of with it.
 *
 * Imperative sizing uses each panel's own `resize()` rather than the group's
 * `setLayout()`. Calling `setLayout` after open/dock changes permanently
 * corrupts the library's bookkeeping for whichever panel sits immediately
 * before the filler in DOM order, which inverts subsequent drag directions.
 */

export type DockedPanelId = "agenda" | "codes";
export type DockedPanelOpenState = Record<DockedPanelId, boolean>;
export type DockedPanelHeights = Record<DockedPanelId, number>;

/**
 * Height a panel gets the first time it expands, before anyone drags it.
 * Tall enough to show a panel header plus several content rows.
 */
export const PANEL_DEFAULT_HEIGHT = 280;

/**
 * Smallest height a dragged panel keeps. Deliberately far below
 * `PANEL_DEFAULT_HEIGHT`: the design lets a panel shrink to roughly its header
 * plus one row, so the floor and the default expanded height are separate.
 */
export const PANEL_MIN_HEIGHT = 100;

/** Height of a collapsed panel — its header and nothing else. */
export const PANEL_COLLAPSED_HEIGHT = 38;

/**
 * Space the filler panel always keeps. Applied as the filler's own `minSize`,
 * which is what stops a panel from growing past `groupHeight - 60` and matches
 * the limit in the design.
 */
export const FILLER_MIN_HEIGHT = 60;

export function panelLayoutId(id: DockedPanelId): string {
  return `${id}-panel`;
}

export interface PanelStackOptions {
  /** Panels in this group, in render order, excluding the filler. */
  members: readonly DockedPanelId[];
  /** Which of those panels are expanded. */
  open: DockedPanelOpenState;
  /**
   * Remembered expanded heights. Shared across stacks by the caller so a panel
   * keeps its height when it moves between the rail and the note column.
   */
  heightsRef: MutableRefObject<DockedPanelHeights>;
}

export type PanelStackRefs = Record<
  DockedPanelId,
  RefObject<PanelImperativeHandle | null>
>;

export function usePanelStack({
  members,
  open,
  heightsRef,
}: PanelStackOptions) {
  const agendaPanelRef = usePanelRef();
  const codesPanelRef = usePanelRef();
  const panelRefs: PanelStackRefs = {
    agenda: agendaPanelRef,
    codes: codesPanelRef,
  };
  const elementRef = useRef<HTMLDivElement | null>(null);
  const openRef = useRef(open);
  const membersRef = useRef(members);
  const applyingLayoutRef = useRef(false);

  const applyLayout = useCallback(
    (nextOpen: DockedPanelOpenState, nextMembers: readonly DockedPanelId[]) => {
      if (nextMembers.length === 0) return;

      applyingLayoutRef.current = true;
      for (const id of nextMembers) {
        const height = nextOpen[id]
          ? heightsRef.current[id]
          : PANEL_COLLAPSED_HEIGHT;
        const panelRef = id === "agenda" ? agendaPanelRef : codesPanelRef;
        panelRef.current?.resize(height);
      }
      // Clear after the library has emitted any programmatic onResize callbacks.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          applyingLayoutRef.current = false;
        });
      });
    },
    [agendaPanelRef, codesPanelRef, heightsRef],
  );

  /**
   * Re-applies panel sizes after the panel set or open state changes.
   *
   * Every pass is deferred past this commit on purpose. When a panel docks in or
   * out, the group only re-registers its panels in the children's own effects,
   * so resizing synchronously here would target the previous set of panels.
   * The later passes then cover the collapse animation, which changes the
   * group's height after the commit that started it.
   */
  useLayoutEffect(() => {
    openRef.current = open;
    membersRef.current = members;

    const apply = () => applyLayout(open, members);
    const frame = requestAnimationFrame(apply);
    const timers = [50, 250].map((delay) => window.setTimeout(apply, delay));

    return function cleanup() {
      cancelAnimationFrame(frame);
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [applyLayout, members, open]);

  /**
   * Records the height a panel currently shows, so it can be restored after a
   * collapse or carried over when the panel docks somewhere else. This is the
   * only thing a drag feeds back into — the library owns the drag itself.
   *
   * The Note tab keeps its PanelGroups laid out while inactive (visibility
   * hidden, not display:none), so tab switches no longer emit zero-height
   * clamp noise that would overwrite these values.
   */
  const rememberHeight = useCallback(
    (id: DockedPanelId, sizeInPixels: number) => {
      if (applyingLayoutRef.current) return;
      if (!openRef.current[id] || sizeInPixels <= PANEL_COLLAPSED_HEIGHT) {
        return;
      }
      heightsRef.current[id] = sizeInPixels;
    },
    [heightsRef],
  );

  return { elementRef, panelRefs, rememberHeight, heightsRef };
}
